import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Stack,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Logo from "../../images/logo1.png";

const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const ClickHandler = () => {
    window.scrollTo(0, 0);
  };

  const footerLinks = {
    services: [
      {
        name: "Business Setup & Compliance",
        link: "/service-group/business-setup-compliance",
      },
      { name: "Advisory & Deals", link: "/service-group/advisory-deals" },
      {
        name: "Audit & Assurance",
        link: "/service-category/auditAndAssurance",
      },
      {
        name: "Taxation & Regulatory",
        link: "/service-category/taxationAndRegulatory",
      },
      {
        name: "Virtual CFO Services",
        link: "/service-category/virtualCfoServices",
      },
    ],
    usefulLinks: [
      { name: "Financial Calculators", link: "/calculator" },
      { name: "Bare Acts & Statutes", link: "/acts" },
      { name: "Resources & Portals", link: "/links" },
      { name: "Current Openings", link: "/currentOpenings" },
      { name: "FAQ Section", link: "/faq" },
    ],
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#1c265a",
        color: "#fff",
        pt: { xs: 8, md: 10 },
        pb: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Gradient Background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "40%",
          height: "100%",
          background:
            "radial-gradient(circle at 100% 0%, rgba(0, 181, 173, 0.05) 0%, transparent 70%)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6}>
          {/* Brand & About Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <RouterLink
                to="/"
                onClick={ClickHandler}
                style={{ textDecoration: "none" }}
              >
                <img
                  src={Logo}
                  alt="Vdoogar Logo"
                  style={{
                    height: "45px",
                    width: "auto",
                    marginBottom: "15px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    display: "block",
                    mt: -1,
                  }}
                >
                  Together Makes Progress
                </Typography>
              </RouterLink>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.6)",
                lineHeight: 1.8,
                mb: 4,
                maxWidth: "320px",
                fontSize: "0.95rem",
              }}
            >
              Excellence with empathy. We provide strategic financial advisory
              and global compliance solutions to empower businesses in a dynamic
              regulatory landscape.
            </Typography>
            <Stack direction="row" spacing={1}>
              {[
                { icon: <WhatsAppIcon />, link: "https://wa.me/919999632411" },
              ].map((social, index) => (
                <IconButton
                  key={index}
                  component="a"
                  href={social.link}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: "rgba(255,255,255,0.8)",
                    transition: "0.3s",
                    "&:hover": {
                      bgcolor: "var(--primary-color)",
                      color: "#fff",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Quick Links Column */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                mb: 4,
                fontFamily: "'Roboto Slab', serif",
                letterSpacing: 0.5,
                color: "var(--tertiary-color)",
              }}
            >
              Services
            </Typography>
            <Stack spacing={2}>
              {footerLinks.services.map((link) => (
                <Box
                  key={link.name}
                  component={RouterLink}
                  to={link.link}
                  onClick={ClickHandler}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    transition: "0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      color: "var(--tertiary-color)",
                      transform: "translateX(5px)",
                    },
                  }}
                >
                  <ArrowForwardIosIcon
                    sx={{ fontSize: "0.6rem", opacity: 0.5 }}
                  />
                  {link.name}
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Useful Links Column */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                mb: 4,
                fontFamily: "'Roboto Slab', serif",
                letterSpacing: 0.5,
                color: "var(--tertiary-color)",
              }}
            >
              Knowledge
            </Typography>
            <Stack spacing={2}>
              {footerLinks.usefulLinks.map((link) => (
                <Box
                  key={link.name}
                  component={RouterLink}
                  to={link.link}
                  onClick={ClickHandler}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    transition: "0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      color: "var(--tertiary-color)",
                      transform: "translateX(5px)",
                    },
                  }}
                >
                  <ArrowForwardIosIcon
                    sx={{ fontSize: "0.6rem", opacity: 0.5 }}
                  />
                  {link.name}
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Contact Column */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                mb: 4,
                fontFamily: "'Roboto Slab', serif",
                letterSpacing: 0.5,
                color: "var(--tertiary-color)",
              }}
            >
              Get In Touch
            </Typography>
            <Stack spacing={3}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <LocationOnIcon
                  sx={{
                    color: "var(--tertiary-color)",
                    fontSize: "1.2rem",
                    mt: 0.5,
                  }}
                />
                <Typography
                  component="a"
                  href="https://maps.app.goo.gl/gTiT588qDCYYakgRA"
                  target="_blank"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.9rem",
                    textDecoration: "none",
                    lineHeight: 1.6,
                  }}
                >
                  4th Floor, 404, Paras Trinity, Sector 63, Gurugram, Haryana,
                  122011
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <PhoneIcon
                  sx={{ color: "var(--tertiary-color)", fontSize: "1.2rem" }}
                />
                <Typography
                  component="a"
                  href="tel:+919999632411"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.9rem",
                    textDecoration: "none",
                  }}
                >
                  +91 999 963 2411
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <EmailIcon
                  sx={{ color: "var(--tertiary-color)", fontSize: "1.2rem" }}
                />
                <Typography
                  component="a"
                  href="mailto:admin@vdoogar.com"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.9rem",
                    textDecoration: "none",
                  }}
                >
                  admin@vdoogar.com
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer;
